import React from 'react';
import styled from 'styled-components';
import { education } from '../../data/constants';
import SectionHeading from '../common/SectionHeading';

const Section = styled.section`
  width: 100%;
  padding: 120px 24px;
  position: relative;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 15% 20%, rgba(133, 76, 230, 0.15), transparent 55%),
    radial-gradient(circle at 80% 0%, rgba(0, 70, 209, 0.2), transparent 45%);
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 40px;
  align-items: flex-start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryPanel = styled.div`
  background: rgba(20, 22, 35, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 28px;
  padding: 28px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 14px;
`;

const HighlightCard = styled.div`
  border-radius: 18px;
  padding: 16px;
  background: rgba(8, 10, 18, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
`;

const HighlightValue = styled.p`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
`;

const HighlightLabel = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;

const FocusList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FocusItem = styled.li`
  display: flex;
  gap: 10px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
`;

const Bullet = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  margin-top: 6px;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: ${({ theme }) => theme.bgLight};
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 34px;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 10px;
    width: 2px;
    height: 100%;
    background: linear-gradient(180deg, rgba(133, 76, 230, 0.4), rgba(0, 70, 209, 0.4));
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 32px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineDot = styled.span`
  position: absolute;
  left: -34px;
  top: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(133, 76, 230, 0.8);
  background: ${({ theme }) => theme.bg};
`;

const TimelineCard = styled.div`
  border-radius: 20px;
  padding: 22px 24px;
  background: rgba(20, 22, 35, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Degree = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
`;

const Date = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;

const School = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
`;

const Grade = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(133, 76, 230, 0.15);
  color: ${({ theme }) => theme.text_primary};
  font-size: 13px;
  width: fit-content;
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
`;

const summaryStats = [
  { value: '4+', label: 'Years of focused AI/ML study' },
  { value: '3', label: 'Capstone & research projects' },
  { value: '5', label: 'Scholarships & accolades' },
];

const focusAreas = [
  'AI/ML specialization at Osmania University',
  'Engineering fundamentals paired with product delivery',
  'Thesis work spanning optimization and generative systems',
];

const Education = () => (
  <Section id="education">
    <Glow />
    <Content>
      <Layout>
        <SummaryPanel>
          <SectionHeading
            eyebrow="Education"
            title="An academic foundation rooted in applied AI"
            description="Structured learning complemented by experimentation across research labs, hackathons, and consulting engagements."
            align="left"
          />
          <HighlightsGrid>
            {summaryStats.map((stat) => (
              <HighlightCard key={stat.label}>
                <HighlightValue>{stat.value}</HighlightValue>
                <HighlightLabel>{stat.label}</HighlightLabel>
              </HighlightCard>
            ))}
          </HighlightsGrid>
          <FocusList>
            {focusAreas.map((item) => (
              <FocusItem key={item}>
                <Bullet />
                {item}
              </FocusItem>
            ))}
          </FocusList>
        </SummaryPanel>
        <Timeline>
          {education.map((item) => (
            <TimelineItem key={item.id || item.school}>
              <TimelineDot />
              <TimelineCard>
                <TimelineHeader>
                  <LogoRow><LogoImage src={item.img} alt={item.school} /> <Degree>{item.degree}</Degree></LogoRow>
                  <Date>{item.date}</Date>
                </TimelineHeader>
                <School>{item.school}</School>
                <Grade>Grade: {item.grade}</Grade>
                <Description>{item.desc}</Description>
              </TimelineCard>
            </TimelineItem>
          ))}
        </Timeline>
      </Layout>
    </Content>
  </Section>
);

export default Education;
